// Storefront safeguard. Hosted-checkout enforcement requires a Shopify validation Function.
const MINIMUM_AED = 7900;

/** Amounts use Shopify currency subunits; delivery charges never qualify. */
export function minimumOrderState(cart, feeIds = [], currencyRate = null) {
  if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) {
    return { allowed: false, reason: 'unverified' };
  }
  const currency = cart.currency;
  if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) return { allowed: false, reason: 'unverified' };
  const minimum = currency === 'AED' ? MINIMUM_AED
    : Number.isFinite(currencyRate) && currencyRate > 0 ? Math.ceil(MINIMUM_AED * currencyRate - 1e-8) : null;
  if (!minimum || !Number.isSafeInteger(minimum)) return { allowed: false, reason: 'unverified' };
  const fees = new Set(feeIds.map(String));
  let feeTotal = 0;
  for (const item of cart.items) {
    if (!fees.has(String(item.variant_id)) && !item.properties?._rdb_delivery_fee) continue;
    if (!Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0) return { allowed: false, reason: 'unverified' };
    // AJAX final_line_price excludes cart-level allocations. Subtracting it is
    // conservative if a stale delivery fee was discounted; delivery.js rejects
    // discounted fees separately before checkout.
    feeTotal += item.final_line_price;
  }
  const subtotal = Math.max(0, cart.total_price - feeTotal);
  return { allowed: subtotal >= minimum, subtotal, minimum, currency, reason: subtotal >= minimum ? null : 'minimum' };
}

const configNode = typeof document === 'undefined' ? null : document.getElementById('rdb-minimum-order-config');
const config = configNode ? JSON.parse(configNode.textContent) : null;
let currentState = { allowed: false, reason: 'unverified' };
let refreshRevision = 0;
let refreshTimer;

function rateFor(currency) {
  return currency && window.Shopify?.currency?.active === currency ? Number(window.Shopify.currency.rate) : null;
}

function renderState(state) {
  currentState = state;
  if (!config) return state;
  for (const notice of document.querySelectorAll('rdb-minimum-order')) {
    const title = notice.querySelector('[data-minimum-title]');
    const message = notice.querySelector('[data-minimum-message]');
    if (!title || !message) continue;
    notice.hidden = state.allowed;
    title.hidden = state.reason === 'unverified';
    message.textContent = config.strings[state.reason === 'unverified' ? 'check_error' : 'message'];
  }
  // The delivery controller owns its additional disabled states. Never unlock it here.
  for (const button of document.querySelectorAll('[data-rdb-minimum-checkout]:not([data-rdb-delivery-required])')) {
    button.disabled = !state.allowed;
  }
  return state;
}

export function updateMinimumOrder(cart) {
  return renderState(config ? minimumOrderState(cart, config.feeIds, rateFor(cart?.currency)) : { allowed: false, reason: 'unverified' });
}

export function requireMinimumOrder(cart) {
  const state = updateMinimumOrder(cart);
  if (!state.allowed) {
    const notice = [...document.querySelectorAll('rdb-minimum-order')].find(node => node.getClientRects().length);
    notice?.focus({ preventScroll: true });
    notice?.scrollIntoView({ behavior: 'instant', block: 'center' });
  }
  return state.allowed;
}

async function readCart() {
  const response = await fetch(`${config.root}cart.js`, {
    credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) throw new Error('minimum-order-read');
  return response.json();
}

async function refresh() {
  const revision = ++refreshRevision;
  try {
    const cart = await readCart();
    if (revision === refreshRevision) updateMinimumOrder(cart);
    return cart;
  } catch {
    if (revision === refreshRevision) renderState({ allowed: false, reason: 'unverified' });
    return null;
  }
}

if (config) {
  class MinimumOrderNotice extends HTMLElement {
    static observedAttributes = ['data-subtotal', 'data-currency'];
    connectedCallback() { this.renderReceipt(); }
    attributeChangedCallback() { if (this.isConnected) this.renderReceipt(); }
    renderReceipt() {
      const subtotal = Number(this.dataset.subtotal);
      if (!Number.isSafeInteger(subtotal) || subtotal < 0) return;
      renderState(minimumOrderState({ total_price: subtotal, currency: this.dataset.currency, items: [] }, [], rateFor(this.dataset.currency)));
    }
  }
  if (!customElements.get('rdb-minimum-order')) customElements.define('rdb-minimum-order', MinimumOrderNotice);
  window.rdbMinimumOrder = { update: updateMinimumOrder, require: requireMinimumOrder, refresh, get state() { return currentState; } };

  const scheduleRefresh = () => {
    ++refreshRevision;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 150);
  };
  for (const name of ['cart:update', 'discount:update', 'rdb:delivery-saved']) document.addEventListener(name, scheduleRefresh);
  window.addEventListener('pageshow', scheduleRefresh);
  window.addEventListener('focus', scheduleRefresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(); });

  // Every theme-owned checkout link goes through the checked cart journey.
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !/\/(?:checkout|checkouts)(?:\/|$)/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(config.cartUrl);
  }, true);

  const approvedSubmissions = new WeakSet();
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/?$/.test(new URL(form.action, location.href).pathname)) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    if (approvedSubmissions.delete(form)) return;
    // Its fresh pre-fee and post-fee checks share this same rule.
    if (typeof document.querySelector('rdb-delivery')?.checkout === 'function') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const cart = await refresh();
    if (!requireMinimumOrder(cart)) return;
    if (config.deliveryEnabled) { window.location.assign(config.cartUrl); return; }
    approvedSubmissions.add(form);
    form.requestSubmit(event.submitter || undefined);
  }, true);
}
